import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  Output,
  ViewChild,
} from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MojoFeatureExtensionsService } from 'mojo-feature-extensions';
import { Observable, of, Subscription } from 'rxjs';
import { filter, map, shareReplay, switchMap, take } from 'rxjs/operators';

import { AppFeatureExtensions } from '@app/app-feature-extensions.config';
import { MojoBatchService } from 'mojo-batch';
import {
  FeedbackMenuBatchOperationModalData,
  FeedbackMenuContentComponent,
} from '@shared/feedback/menu-content/feedback-menu-content.component';
import { FeedbackTag } from '@shared/feedback/menu-content/feedback-menu.data';
import { DEFAULT_TEXTS } from '@shared/hiring-manager-review-button/button-text-config';
import {
  HM_BAD_MATCH_REASONS_BASIC,
  HM_BAD_MATCH_REASONS_ORACLE,
  HM_GOOD_MATCH_REASONS,
} from '@shared/hiring-manager-review-button/hiring-manager-review-config';
import { Feedback, FeedbackType, ThumbValue } from '@shared/models';

@Component({
  selector: 'mojo-batch-operation-advance-reject-hiring-manager',
  templateUrl: './batch-operation-advance-reject-hiring-manager.component.html',
  styleUrls: ['./batch-operation-advance-reject-hiring-manager.component.scss'],
})
export class BatchOperationAdvanceRejectHiringManagerComponent implements OnDestroy {
  @Input() jobId: number;
  @Output() feedbackSent: EventEmitter<Feedback[]> = new EventEmitter<Feedback[]>();

  @ViewChild('rejectButton', { static: false }) rejectButtonRef: ElementRef;
  @ViewChild('advanceButton', { static: false }) advanceButtonRef: ElementRef;

  disabled$ = this.selectionManagerService.selection$.pipe(
    map((selection) => selection.length === 0),
    shareReplay(1),
  );

  private isOracleATS$ = this.featureExtensionsService
    .hasFeatureExtension(AppFeatureExtensions.ORACLE_ATS)
    .pipe(shareReplay(1));

  private subscription: Subscription = new Subscription();

  constructor(
    private selectionManagerService: MojoBatchService,
    private dialog: MatDialog,
    public featureExtensionsService: MojoFeatureExtensionsService,
  ) {}

  onBtnClick(isAdvance: boolean) {
    this.disabled$
      .pipe(
        switchMap((disabled) => (disabled ? of([]) : this.selectionManagerService.selection$)),
        take(1),
        filter((items) => items.length > 0),
      )
      .subscribe((candidateIds: string[]) =>
        this.openModal(
          candidateIds.filter((id) => !!id).map((id) => +id),
          isAdvance,
        ),
      );
  }

  private openModal(candidateIds: number[], isAdvance: boolean) {
    this.subscription.add(
      this.reasonTags().subscribe((reasonTags) => {
        this.openDialog(candidateIds, isAdvance, reasonTags);
      }),
    );
  }

  private openDialog(candidateIds: number[], isAdvance: boolean, reasonTags: FeedbackTag[]) {
    const currentDialogHeightRem = 12.75;
    const dialogRef = this.dialog.open<
      FeedbackMenuContentComponent,
      FeedbackMenuBatchOperationModalData
    >(FeedbackMenuContentComponent, {
      panelClass: 'batch-operation-advance-reject',
      backdropClass: 'batch-operation-advance-reject-backdrop',
      disableClose: false,
      height: `${currentDialogHeightRem}rem`,
      data: {
        // todo MOJAT-4132 change menu type to FeedbackMenuType.TEXT_AREA
        feedbackModels: this.getFeedbackModel(candidateIds, isAdvance),
        title: isAdvance ? DEFAULT_TEXTS.menuThumbUpTitle : DEFAULT_TEXTS.menuThumbDownTitle,
        thumbIcon: isAdvance ? ThumbValue.UP : ThumbValue.DOWN,
        reasonTags: isAdvance ? HM_GOOD_MATCH_REASONS : reasonTags,
        resultType: isAdvance
          ? FeedbackType.HIRING_MANAGER_GOOD_MATCH_NOTE
          : FeedbackType.HIRING_MANAGER_BAD_MATCH_REASON,
        raisedThumbButton: true,
        positionRelativeTo: isAdvance ? this.advanceButtonRef : this.rejectButtonRef,
        currentDialogHeightRem,
      },
    });
    this.listenOnFeedbackSent(dialogRef);
  }

  private listenOnFeedbackSent(dialogRef: MatDialogRef<FeedbackMenuContentComponent>) {
    this.subscription.add(
      dialogRef.componentInstance.feedbackSent.subscribe((feedback: Feedback[]) => {
        this.feedbackSent.emit(feedback);
      }),
    );
  }

  private reasonTags(): Observable<FeedbackTag[]> {
    return this.isOracleATS$.pipe(
      map((oracle) => (oracle ? HM_BAD_MATCH_REASONS_ORACLE : HM_BAD_MATCH_REASONS_BASIC)),
    );
  }

  private getFeedbackModel(candidateIds: number[], isAdvance: boolean): Feedback[] {
    const result: Feedback[] = [];
    candidateIds.forEach((candidateId) => {
      result.push({
        jobId: this.jobId,
        candidateId,
        type: isAdvance
          ? FeedbackType.HIRING_MANAGER_GOOD_MATCH_NOTE
          : FeedbackType.HIRING_MANAGER_BAD_MATCH_REASON,
      });
    });
    return result;
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
  }
}
